import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';

const MOTION_SOURCE = 'https://www.w3.org/TR/device-motion/';

/**
 * Device motion sensor noise (novel_bot_detection §3B).
 *
 * Physical MEMS accelerometers/gyroscopes show continuous stochastic noise,
 * zero-g offset drift, and thermal variance. Emulated environments (DevTools
 * device emulation, cloud farms) often return either a perfectly static vector
 * (e.g. 0, 0, 9.81) or a mathematically smooth synthetic sine with near-zero
 * residual.
 *
 * This probe runs on every scan. On iOS Safari the motion permission prompt
 * requires a user gesture, so it reports "denied"/"waiting" there; elsewhere
 * DeviceMotionEvent fires on its own and the samples are collected passively.
 */
export const motionProbe: Probe = {
  id: 'motion',
  label: 'Device motion noise',
  layer: 'hardware',
  tier: 'client',
  category: 'hardware',
  description: 'Physical vs synthetic accelerometer/gyroscope noise',
  budgetMs: 6000,
  // NOT requiresGesture. DeviceMotionEvent fires without a user gesture in
  // every browser except iOS Safari, which requires a permission prompt — and
  // that prompt is itself a gesture-gated API, handled inside run() below. So
  // gating the whole probe on a gesture would have thrown away the data on
  // every other platform. It previously ran only from the /behavior page,
  // which meant it was skipped on a default scan; it now runs everywhere and
  // reports "waiting"/"denied"/"unsupported" honestly.
  requiresGesture: false,

  async run(): Promise<ProbeResult> {
    // The permission prompt is itself gesture-gated in iOS Safari; elsewhere
    // DeviceMotionEvent just doesn't fire without sensors. We handle both.
    const hasDME = typeof DeviceMotionEvent !== 'undefined';
    if (!hasDME) {
      return {
        signals: {
          supported: na<boolean>('unsupported', 'client', 'DeviceMotionEvent unavailable'),
        },
      };
    }

    // Ask for permission where the API requires it.
    const requestPerm = (
      DeviceMotionEvent as unknown as { requestPermission?: () => Promise<PermissionState> }
    ).requestPermission;
    if (typeof requestPerm === 'function') {
      try {
        const state = await requestPerm.call(DeviceMotionEvent);
        if (state !== 'granted') {
          return {
            signals: {
              supported: ok(true, 'client'),
              permission: ok(state, 'client'),
              samples: na<unknown>('denied', 'client', 'motion permission denied'),
            },
          };
        }
      } catch (err) {
        return {
          signals: {
            supported: ok(true, 'client'),
            permission: na<string>('denied', 'client', String(err)),
          },
        };
      }
    }

    // Collect ~2 s of samples.
    const accel: Array<{ x: number; y: number; z: number }> = [];
    const rot: Array<{ alpha: number; beta: number; gamma: number }> = [];
    const handler = (e: DeviceMotionEvent) => {
      const a = e.accelerationIncludingGravity;
      if (a && a.x != null) accel.push({ x: a.x, y: a.y ?? 0, z: a.z ?? 0 });
      const r = e.rotationRate;
      if (r && (r.alpha != null || r.beta != null || r.gamma != null)) {
        rot.push({ alpha: r.alpha ?? 0, beta: r.beta ?? 0, gamma: r.gamma ?? 0 });
      }
    };
    window.addEventListener('devicemotion', handler);

    await new Promise<void>((resolve) => setTimeout(resolve, 2000));
    window.removeEventListener('devicemotion', handler);

    if (accel.length === 0) {
      return {
        signals: {
          supported: ok(true, 'client'),
          samples: na<number>('unavailable', 'client', 'no motion events fired (device has no sensors, or permission not granted)'),
        },
      };
    }

    // --- classify the noise character --------------------------------------
    // Physical MEMS: small, non-zero, non-repeating jitter around gravity.
    // Static emulator: identical vectors (zero variance).
    // Synthetic: a generated waveform stays smooth EVEN AFTER differencing.
    //
    // The correlation runs on FIRST DIFFERENCES, not raw z values. Slow, smooth
    // REAL movement (a phone being lifted) has highly correlated raw values
    // too, so the raw test flagged genuine motion as synthetic. Differencing
    // removes the slow trend; what remains is dominated by sensor noise on real
    // hardware, while a mathematically generated waveform stays correlated.
    const { classification, accelVariance, uniqueVectors, firstDiffCorrelation } =
      classifyMotion(accel);

    const findings: Finding[] = [];
    if (classification === 'static' && accel.length > 20) {
      findings.push({
        id: 'motion.static-vector',
        severity: 'medium',
        category: 'hardware',
        layer: 'hardware',
        confidence: 'medium',
        title: 'Device motion reports a perfectly static vector',
        detail:
          'A real MEMS accelerometer always shows low-level stochastic noise, zero-g offset drift, and thermal variance. A completely static reading across many samples indicates a synthetic sensor, typical of device emulation or a cloud device farm.',
        sources: [MOTION_SOURCE],
        evidence: [
          `samples=${accel.length}`,
          `uniqueVectors=${uniqueVectors}`,
          `zVariance=${sig(accelVariance, 4)}`,
        ],
        scoreContribution: 20,
      });
    } else if (classification === 'synthetic') {
      findings.push({
        id: 'motion.synthetic-signal',
        severity: 'medium',
        category: 'hardware',
        layer: 'hardware',
        confidence: 'low',
        title: 'Device motion signal looks synthetic',
        detail:
          'First differences of the motion samples remain near-perfectly correlated. Differencing removes slow smooth movement, so a real sensor’s noise would destroy that correlation; a generated waveform stays smooth at every order. This is low confidence on its own: a device could be receiving a software-filtered signal for legitimate reasons (vibration damping, fused sensor APIs).',
        sources: [MOTION_SOURCE],
        evidence: [`firstDiffCorrelation=${sig(firstDiffCorrelation, 4)}`, `samples=${accel.length}`],
        scoreContribution: 0,
      });
    }

    return {
      signals: {
        supported: ok(true, 'client'),
        sampleCount: ok(accel.length, 'client'),
        accelZMean: ok(sig(mean(accel.map((s) => s.z)), 3), 'client'),
        accelZVariance: ok(sig(accelVariance, 4), 'derived'),
        uniqueVectors: ok(uniqueVectors, 'derived'),
        // Correlation of first differences (detrended): the synthetic test.
        firstDiffCorrelation: ok(sig(firstDiffCorrelation, 4), 'derived'),
        hasRotation: ok(rot.length > 0, 'client'),
        classification: ok(classification, 'derived'),
      },
      findings,
      // NO part contribution. The motion classification reflects DEVICE STATE
      // AT THE MOMENT OF SAMPLING (a phone on a table vs in a hand), not a
      // stable property of the device, so hashing it would make the
      // fingerprint change every time the device moved. The classification is
      // reported as a signal; it must not enter the hash.
    };
  },
};

export type MotionClassification = 'physical-mems' | 'static' | 'synthetic' | 'noisy-unknown';

export interface MotionAnalysis {
  classification: MotionClassification;
  accelVariance: number;
  uniqueVectors: number;
  firstDiffCorrelation: number;
}

/**
 * Classify accelerometer samples. Pure and exported for unit tests.
 *
 * The synthetic test is Pearson correlation of the FIRST DIFFERENCES, not of
 * the raw z series: raw slow movement is naturally correlated and the raw test
 * produced false positives on smooth real-like data.
 */
export function classifyMotion(
  samples: Array<{ x: number; y: number; z: number }>,
): MotionAnalysis {
  const zs = samples.map((s) => s.z);
  const accelVariance = variance(zs);
  const uniqueVectors = new Set(
    samples.map((s) => `${s.x.toFixed(3)},${s.y.toFixed(3)},${s.z.toFixed(3)}`),
  ).size;
  const isStatic = accelVariance < 1e-6 || uniqueVectors <= 2;
  const firstDiffCorrelation = consecutiveCorrelation(firstDifferences(zs));
  const isSynthetic = !isStatic && firstDiffCorrelation > 0.98;

  const classification: MotionClassification = isStatic
    ? 'static'
    : isSynthetic
      ? 'synthetic'
      : accelVariance > 0.01
        ? 'physical-mems'
        : 'noisy-unknown';

  return { classification, accelVariance, uniqueVectors, firstDiffCorrelation };
}

/** Successive differences of a series. */
export function firstDifferences(xs: number[]): number[] {
  const out: number[] = [];
  for (let i = 1; i < xs.length; i++) out.push(xs[i] - xs[i - 1]);
  return out;
}

function mean(xs: number[]): number {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
}

function variance(xs: number[]): number {
  if (xs.length < 2) return 0;
  const m = mean(xs);
  return xs.reduce((a, b) => a + (b - m) ** 2, 0) / xs.length;
}

export function consecutiveCorrelation(xs: number[]): number {
  if (xs.length < 3) return 0;
  // Pearson correlation between xs[0..n-2] and xs[1..n-1].
  const a = xs.slice(0, -1);
  const b = xs.slice(1);
  const ma = mean(a);
  const mb = mean(b);
  let num = 0;
  let da = 0;
  let db = 0;
  for (let i = 0; i < a.length; i++) {
    const x = a[i] - ma;
    const y = b[i] - mb;
    num += x * y;
    da += x * x;
    db += y * y;
  }
  const den = Math.sqrt(da * db);
  return den === 0 ? 0 : num / den;
}

registerProbe(motionProbe);
